import React, { useState, useCallback, useRef, useEffect } from 'react';
import { withRouter } from 'react-router';
import { translateInterfaceText } from 'helpers/translation';
import { Button, Box, red, grey400, grey600 } from 'dma-ui-kit';
import styled from 'styled-components';

const InputContainer = styled(Box)`
  display: flex;
  padding: 10px 0;
  align-items: baseline;
  position: relative;
  width: 100%;
`;

const SuggestionsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid ${grey400};
  border-top: none;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const SuggestionItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid ${grey400};
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${grey600};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const SuggestionText = styled.div`
  font-size: 1.2rem;
  font-weight: 400;
  color: ${red};
  margin-bottom: 4px;
`;

const Input = styled.input`
  font-family: 'usual';
  font-weight: 400;
  font-size: 2rem;
  width: 100%;
  font-style: italic;
  margin-right: 20px;
  box-sizing: border-box;
  padding: 10px;
  color: ${red};
  background-color: transparent;
  border: none;
  border-bottom: 4px solid ${red};

  &:focus {
    outline: none;
    border-bottom: 4px solid ${red};
  }

  ::placeholder {
    font-style: none;
    color: ${grey400};
  }
`;

const CollectionSearchBar = props => {
    const { history } = props;
    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const debounceTimeoutRef = useRef(null);
    const suggestionsRef = useRef(null);
    const suggestURL = `${process.env.REACT_APP_SITESEARCH_BASE_PATH}api/search/collection/suggest`;

    const placeholder = translateInterfaceText('Search the collection');
    const label = translateInterfaceText('Search');

    const fetchSuggestions = useCallback(async (term) => {
        if (!term || term.length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        try {
            const response = await fetch(`${suggestURL}?q=${encodeURIComponent(term)}`);
            if (response.ok) {
                const data = await response.json();
                if (data.suggestions && Array.isArray(data.suggestions)) {
                    setSuggestions(data.suggestions);
                    setShowSuggestions(true);
                }
            }
        } catch (error) {
            console.error('Error fetching suggestions:', error);
            setSuggestions([]);
        }
    }, [suggestURL]);

    const debouncedFetchSuggestions = useCallback((term) => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        
        debounceTimeoutRef.current = setTimeout(() => {
            fetchSuggestions(term);
        }, 300);
    }, [fetchSuggestions]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    const setSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedFetchSuggestions(value);
    };

    const search = () => {
        history.push(`/art/collection/search?s=${searchTerm}`);
        setShowSuggestions(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            search();
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setSearchTerm(suggestion);
        setShowSuggestions(false);
        history.push(`/art/collection/search?s=${suggestion}`);
    };

    return (
        <>
            <div>
                <div style={{ display: "block", width: '50%', margin: 'auto' }}>
                    <InputContainer ref={suggestionsRef}>
                        <Input
                            placeholder={placeholder}
                            onChange={setSearch}
                            onKeyDown={handleKeyDown}
                            onFocus={() => {
                                if (suggestions.length > 0 && searchTerm.length >= 2) {
                                    setShowSuggestions(true);
                                }
                            }}
                            value={searchTerm}
                        />
                        <Box>
                            <Button
                                variant="outline"
                                label={label}
                                onClick={search}
                                compact={true}
                                color={red}
                            />
                        </Box>
                        {showSuggestions && suggestions.length > 0 && (
                            <SuggestionsDropdown>
                                {suggestions.map((suggestion, index) => (
                                    <SuggestionItem
                                        key={index}
                                        onClick={() => handleSuggestionClick(suggestion)}
                                    >
                                        <SuggestionText>{suggestion}</SuggestionText>
                                    </SuggestionItem>
                                ))}
                            </SuggestionsDropdown>
                        )}
                    </InputContainer>
                </div>
            </div>
        </>
    );
};

export default withRouter(CollectionSearchBar);